import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { SearchInput } from '@aimsos/ui';

import EntityListSkeleton from '../components/EntityListSkeleton.jsx';
import NoteInsightGroup from '../components/history/NoteInsightGroup.jsx';
import NotesFeedItem from '../components/history/NotesFeedItem.jsx';
import NotesFeedQuickFilters from '../components/history/NotesFeedQuickFilters.jsx';
import { EmptyState, ErrorState } from '../components/states/index.js';
import { NOTES_SEARCH_FIELDS } from '../constants/history.js';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useNotesFeed } from '../hooks/useNotesFeed.js';

import { NotebookText } from 'lucide-react';

const FILTER_DEFAULTS = Object.freeze({ searchFields: NOTES_SEARCH_FIELDS });

/**
 * History › From notes (CT-5724): meeting insights and the Concierge's readings
 * — summaries, topics, interpretations. Email and call summaries are on the
 * Timeline. Two sections, as in the prototype: Meeting insights and Other notes.
 *
 * A search box sits between the subtab switch and the actions, as in the
 * prototype; it narrows on the server, debounced like every list here.
 */
function HistoryNotesPage() {
  const { t } = useTranslation('unified-profiles');
  const { entityType, entityId, subtabSwitch, toolbar } = useOutletContext();
  const [quickFilter, setQuickFilter] = useState('all');
  const { searchTerm, setSearchTerm, search } = useDataListFilters(FILTER_DEFAULTS);
  const handleSearch = useCallback((event) => setSearchTerm(event?.target?.value ?? ''), [setSearchTerm]);
  const clearSearch = useCallback(() => setSearchTerm(''), [setSearchTerm]);
  const { feed, loading, error, refetch } = useNotesFeed({ entityType, entityId, quickFilter, search });

  const body = () => {
    if (loading) return <EntityListSkeleton perPage={4} />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (!feed.insights.length && !feed.other.length) {
      return <EmptyState icon={NotebookText} title={t('history.notes.empty.title')} subtitle={t('history.notes.empty.subtitle')} />;
    }
    return (
      <div data-testid="history-notes-list" className="flex flex-col gap-6">
        {feed.insights.length ? (
          <section aria-label={t('history.notes.sections.insights')} className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted">
              {t('history.notes.sections.insights')}
            </h3>
            {feed.insights.map((insight) => (
              <NoteInsightGroup key={insight.id} insight={insight} />
            ))}
          </section>
        ) : null}
        {feed.other.length ? (
          <section aria-label={t('history.notes.sections.other')} className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted">
              {t('history.notes.sections.other')}
            </h3>
            {feed.other.map((note) => (
              <NotesFeedItem key={note.id} note={note} />
            ))}
          </section>
        ) : null}
      </div>
    );
  };

  return (
    <div data-testid="history-notes" className="flex min-w-0 flex-1 flex-col gap-4 pb-6">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        {subtabSwitch}
        <SearchInput
          value={searchTerm}
          onChange={handleSearch}
          onClear={clearSearch}
          placeholder={t('history.notes.searchPlaceholder')}
          aria-label={t('history.notes.searchPlaceholder')}
          className="min-w-[200px] max-w-xs flex-1"
        />
        <div className="ml-auto">{toolbar}</div>
      </div>
      <NotesFeedQuickFilters value={quickFilter} onChange={setQuickFilter} />
      <p className="text-sm text-content-secondary">{t('history.notes.explanation')}</p>
      <div className="flex min-w-0 flex-1 flex-col">{body()}</div>
    </div>
  );
}

export default HistoryNotesPage;
